import React, {useState} from "react";
import {useAPI, useTitle} from "../stateHelpers";
import CircularLoader from "./CircularLoader";
import FailedLoading from "./FailedLoading";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Accordion from "@material-ui/core/ExpansionPanel";
import AccordionSummary from "@material-ui/core/ExpansionPanelSummary";
import AccordionDetails from "@material-ui/core/ExpansionPanelDetails";
import ExpandMore from "@material-ui/icons/ExpandMore";
import Button from "@material-ui/core/Button";

const cellStyle = {fontSize: 12, padding: "6px 10px", verticalAlign: "top"};

function fmt(t) {
    if (!t) return "—";
    let d = new Date(t);
    if (isNaN(d.getTime()) || d.getFullYear() < 2000) return "—";
    return d.toISOString().replace("T", " ").replace(/\..+/, "");
}

function rel(t) {
    if (!t) return "";
    let d = new Date(t).getTime();
    if (!d) return "";
    let s = Math.round((Date.now() - d) / 1000);
    let abs = Math.abs(s);
    let sign = s >= 0 ? "ago" : "from now";
    if (abs < 60) return `${abs}s ${sign}`;
    if (abs < 3600) return `${Math.round(abs / 60)}m ${sign}`;
    if (abs < 86400) return `${Math.round(abs / 3600)}h ${sign}`;
    return `${Math.round(abs / 86400)}d ${sign}`;
}

const STATUS_COLOR = {
    done: {bg: "#c8e6c9", fg: "#1b5e20"},
    failed: {bg: "#ffcdd2", fg: "#b71c1c"},
    pending: {bg: "#fff9c4", fg: "#827717"},
    queued: {bg: "#e0e0e0", fg: "#212121"},
    expired: {bg: "#d1c4e9", fg: "#311b92"},
};

function StatusChip({status}) {
    let c = STATUS_COLOR[status] || STATUS_COLOR.queued;
    return <Chip size="small" label={status}
                 style={{background: c.bg, color: c.fg, fontWeight: 600, height: 20}}/>;
}

const KIND_COLOR = {
    request_created: "#1976d2",
    request_updated: "#90a4ae",
    signer_emailed: "#0288d1",
    signer_email_viewed: "#26a69a",
    signer_viewed: "#26a69a",
    signer_downloaded: "#7e57c2",
    signer_downloaded_log: "#7e57c2",
    signer_signed: "#2e7d32",
    signer_declined: "#c62828",
    reminder_enqueued: "#ef6c00",
    email_enqueued: "#0288d1",
    webhook_enqueued: "#5d4037",
    task_done: "#2e7d32",
    task_failed: "#c62828",
    task_enqueued: "#616161",
};

function kindColor(k) {
    if (KIND_COLOR[k]) return KIND_COLOR[k];
    if (k && k.indexOf("log:") === 0) return "#455a64";
    return "#616161";
}

function JsonBlock({value}) {
    if (value === undefined || value === null) return null;
    let s = typeof value === "string" ? value : JSON.stringify(value, null, 2);
    return <pre style={{
        margin: 0, padding: 8, background: "#fafafa", border: "1px solid #eee",
        fontSize: 11, overflowX: "auto", maxHeight: 300
    }}>{s}</pre>;
}

function Section({title, children, defaultExpanded = true}) {
    return <Accordion defaultExpanded={defaultExpanded} style={{margin: "12px 0"}}>
        <AccordionSummary expandIcon={<ExpandMore/>}>
            <Typography variant="h6">{title}</Typography>
        </AccordionSummary>
        <AccordionDetails style={{display: "block", padding: "0 16px 16px"}}>
            {children}
        </AccordionDetails>
    </Accordion>;
}

function DebugTimelinePage(props) {
    let id = props.match.params.id;
    let [, setTitle] = useTitle();
    React.useEffect(() => setTitle(`Debug · ${id}`), [id]);
    let [taskFilter, setTaskFilter] = useState("");
    let [showRaw, setShowRaw] = useState(false);

    let {data, error, isLoading} = useAPI(`sign_request/${id}/debug`);
    if (isLoading) return <CircularLoader/>;
    if (error || !data) return <FailedLoading/>;

    let sr = data.sign_request || {};
    let signers = data.signers || [];
    let tasks = data.queued_tasks || [];
    let timeline = data.timeline || [];
    let schedule = data.reminder_schedule || [];
    let logs = data.generic_logs || [];

    let taskNames = Array.from(new Set(tasks.map(t => t.name))).sort();
    let filteredTasks = taskFilter ? tasks.filter(t => t.name === taskFilter) : tasks;

    return <div style={{padding: 20, maxWidth: 1400, margin: "0 auto"}}>
        <Typography variant="h4" gutterBottom>Sign Request Debug</Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
            unique_id: <code>{sr.unique_id}</code> · id: {sr.id} · state: <b>{sr.state}</b>
        </Typography>

        <Section title="Overview">
            <Table size="small">
                <TableBody>
                    {[
                        ["Created", `${fmt(sr.created_at)} (${rel(sr.created_at)})`],
                        ["Updated", `${fmt(sr.updated_at)} (${rel(sr.updated_at)})`],
                        ["Expire", fmt(sr.expire_date)],
                        ["State", sr.state],
                        ["send_reminders", String(sr.send_reminders)],
                        ["callback_url", sr.callback_url || "—"],
                        ["api_version", sr.api_version],
                        ["user_id", sr.user_id],
                        ["identity_id", sr.identitiy_id],
                    ].map(([k, v]) =>
                        <TableRow key={k}>
                            <TableCell style={{...cellStyle, width: 180, fontWeight: 600}}>{k}</TableCell>
                            <TableCell style={cellStyle}>{v}</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Section>

        <Section title={`Signers (${signers.length})`}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {["#", "email", "seq", "emailed", "viewed", "signed", "declined", "downloaded", "reminders", "next reminder"].map(h =>
                            <TableCell key={h} style={{...cellStyle, fontWeight: 700}}>{h}</TableCell>)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {signers.map((s, i) =>
                        <TableRow key={s.id}>
                            <TableCell style={cellStyle}>{i + 1}</TableCell>
                            <TableCell style={cellStyle}>{s.email}</TableCell>
                            <TableCell style={cellStyle}>{s.sequence}</TableCell>
                            <TableCell style={cellStyle}>{fmt(s.emailed)}</TableCell>
                            <TableCell style={cellStyle}>{fmt(s.viewed)}</TableCell>
                            <TableCell style={cellStyle}>{fmt(s.signed)}</TableCell>
                            <TableCell style={cellStyle}>{fmt(s.declined)}</TableCell>
                            <TableCell style={cellStyle}>{fmt(s.downloaded)}</TableCell>
                            <TableCell style={cellStyle}>{s.sent_reminder_count}</TableCell>
                            <TableCell style={cellStyle}>{fmt(s.send_next_reminder)}</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Section>

        <Section title="Reminder schedule">
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {["signer", "sent / cap", "next fire", "will fire?", "reason"].map(h =>
                            <TableCell key={h} style={{...cellStyle, fontWeight: 700}}>{h}</TableCell>)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {schedule.map(r =>
                        <TableRow key={r.signer_id}>
                            <TableCell style={cellStyle}>{r.email}</TableCell>
                            <TableCell style={cellStyle}>{r.sent_reminder_count} / {r.cap}</TableCell>
                            <TableCell style={cellStyle}>{fmt(r.next_reminder)}</TableCell>
                            <TableCell style={cellStyle}>
                                <Chip size="small" label={r.will_fire ? "yes" : "no"}
                                      style={{
                                          background: r.will_fire ? "#c8e6c9" : "#ffcdd2",
                                          color: r.will_fire ? "#1b5e20" : "#b71c1c",
                                          height: 20
                                      }}/>
                            </TableCell>
                            <TableCell style={cellStyle}>{r.reason}</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Section>

        <Section title={`Task queue (${tasks.length})`}>
            <div style={{marginBottom: 8}}>
                <Chip label="all" size="small" onClick={() => setTaskFilter("")}
                      color={taskFilter === "" ? "primary" : "default"}
                      style={{marginRight: 4}}/>
                {taskNames.map(n =>
                    <Chip key={n} label={n} size="small" onClick={() => setTaskFilter(n)}
                          color={taskFilter === n ? "primary" : "default"}
                          style={{marginRight: 4, marginBottom: 4}}/>
                )}
            </div>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {["status", "name", "reference", "created", "run_at", "finished", "retries", "last_error"].map(h =>
                            <TableCell key={h} style={{...cellStyle, fontWeight: 700}}>{h}</TableCell>)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredTasks.map(t =>
                        <React.Fragment key={t.id}>
                            <TableRow>
                                <TableCell style={cellStyle}><StatusChip status={t.status}/></TableCell>
                                <TableCell style={cellStyle}>{t.name}</TableCell>
                                <TableCell style={cellStyle}>{t.reference}</TableCell>
                                <TableCell style={cellStyle}>{fmt(t.created_at)}</TableCell>
                                <TableCell style={cellStyle}>{fmt(t.run_at)}</TableCell>
                                <TableCell style={cellStyle}>{fmt(t.finished)}</TableCell>
                                <TableCell style={cellStyle}>{t.retries}</TableCell>
                                <TableCell style={{...cellStyle, color: "#b71c1c", maxWidth: 280, wordBreak: "break-word"}}>
                                    {t.last_error || ""}
                                </TableCell>
                            </TableRow>
                            {(t.input || t.result) && <TableRow>
                                <TableCell style={cellStyle} colSpan={8}>
                                    <Accordion style={{boxShadow: "none"}}>
                                        <AccordionSummary expandIcon={<ExpandMore/>} style={{minHeight: 24}}>
                                            <Typography variant="caption">input / result</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails style={{display: "block"}}>
                                            {t.input && <><Typography variant="caption">input</Typography><JsonBlock value={t.input}/></>}
                                            {t.result && <><Typography variant="caption">result</Typography><JsonBlock value={t.result}/></>}
                                        </AccordionDetails>
                                    </Accordion>
                                </TableCell>
                            </TableRow>}
                        </React.Fragment>
                    )}
                </TableBody>
            </Table>
        </Section>

        <Section title={`Timeline (${timeline.length})`}>
            <div style={{borderLeft: "2px solid #e0e0e0", paddingLeft: 16}}>
                {timeline.map((ev, i) =>
                    <div key={i} style={{marginBottom: 12, position: "relative"}}>
                        <div style={{
                            position: "absolute", left: -22, top: 4, width: 10, height: 10,
                            borderRadius: "50%", background: kindColor(ev.kind)
                        }}/>
                        <div style={{fontSize: 11, color: "#757575"}}>
                            {fmt(ev.at)} · {rel(ev.at)} · <span style={{color: kindColor(ev.kind)}}>{ev.kind}</span> · {ev.source}#{ev.source_id}
                        </div>
                        <div style={{fontSize: 13, fontWeight: 600}}>{ev.title}</div>
                        {ev.detail && Object.keys(ev.detail).length > 0 &&
                            <Accordion style={{boxShadow: "none", background: "transparent"}}>
                                <AccordionSummary expandIcon={<ExpandMore/>} style={{minHeight: 24, padding: 0}}>
                                    <Typography variant="caption">detail</Typography>
                                </AccordionSummary>
                                <AccordionDetails style={{display: "block", padding: 0}}>
                                    <JsonBlock value={ev.detail}/>
                                </AccordionDetails>
                            </Accordion>
                        }
                    </div>
                )}
            </div>
        </Section>

        <Section title={`Generic logs (${logs.length})`} defaultExpanded={false}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {["created", "action", "reference", "data"].map(h =>
                            <TableCell key={h} style={{...cellStyle, fontWeight: 700}}>{h}</TableCell>)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {logs.map(l =>
                        <TableRow key={l.id}>
                            <TableCell style={cellStyle}>{fmt(l.created_at)}</TableCell>
                            <TableCell style={cellStyle}>{l.action}</TableCell>
                            <TableCell style={cellStyle}>{l.reference}</TableCell>
                            <TableCell style={cellStyle}><JsonBlock value={l.data || l.data_raw}/></TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Section>

        <Section title="Raw dump" defaultExpanded={false}>
            <Button size="small" onClick={() => setShowRaw(!showRaw)}>
                {showRaw ? "hide" : "show"} raw JSON
            </Button>
            {showRaw && <JsonBlock value={data}/>}
        </Section>
    </div>;
}

export default DebugTimelinePage;
